// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { SelectItem } from '@advisor-app/components';
import {
  type AllBenchmarks,
  formattedBenchmarks,
  selectPopularInternationalIndexes,
  selectPopularThematics,
  selectPopularUsIndexes,
} from '@advisor-app/features/utils/benchmark-formatter';
import { selectBenchmark } from '@advisor-app/store/slices/parameters';
import { useAppSelector } from '@advisor-app/store/store';
import type { PopupsName } from '@shared/ui-kit/components';
import {
  AppIcon,
  Heading,
  SectionContainer,
  useInfoPopup,
} from '@shared/ui-kit/components';
import { type FC } from 'react';
import { ButtonCustomizeModal } from './button-customize-modal';
import { SearchUniverse } from './search-universe';
import { useTranslation } from 'react-i18next';

interface BenchmarkSelectorProps {
  handleSelectBenckmark: (benchmark: string) => void;
  readonly assetType: string;
  readonly customize?: boolean;
}

export interface TickerIcons {
  [key: string]: JSX.Element;
}

export const BenchmarkSelector = ({
  handleSelectBenckmark,
  assetType,
  customize = true,
}: BenchmarkSelectorProps) => {
  const { t } = useTranslation();
  const popularUsIndexes = useAppSelector(selectPopularUsIndexes);
  const popularInternationalIndexes = useAppSelector(
    selectPopularInternationalIndexes,
  );
  const popularThematics = useAppSelector(selectPopularThematics);
  const [benchmarkSelected] = useAppSelector(state =>
    selectBenchmark(state, 'ai-builder'),
  );
  const type = assetType === 'stock' ? 'Universe' : 'Benchmark';

  return (
    <SectionContainer
      title={t('ai-builder.select-type', { ns: 'ai-builder', type }) as string}
      variant="h3"
      popupName="ai-builder.selectUniverse"
      tooltip
      className="relative"
      headerClass="mb-5"
      actions={customize && <ButtonCustomizeModal />}
    >
      <div className="grid gap-4">
        <SearchUniverse assetType={assetType} />
        <IndexGroup
          title={t('ai-builder.popular-us-indexes', { ns: 'ai-builder' }) as string}
          indexes={popularUsIndexes}
          benchmarkSelected={benchmarkSelected.ticker}
          handleSelectBenckmark={handleSelectBenckmark}
          rightIcon={<AppIcon icon="USAFlag" />}
        />

        <IndexGroup
          title={
            t('ai-builder.popular-international-indexes', {
              ns: 'ai-builder',
            }) as string
          }
          indexes={popularInternationalIndexes}
          benchmarkSelected={benchmarkSelected.ticker}
          handleSelectBenckmark={handleSelectBenckmark}
          showPopup={false}
        />

        <IndexGroup
          title={t('ai-builder.popular-thematics', { ns: 'ai-builder' }) as string}
          indexes={popularThematics}
          benchmarkSelected={benchmarkSelected.ticker}
          handleSelectBenckmark={handleSelectBenckmark}
          rightIcon={<AppIcon icon="Info" color="disabled" size="md" />}
        />
      </div>
    </SectionContainer>
  );
};

interface IndexGroupProps {
  title: string;
  indexes: { key: string; ticker: string }[];
  handleSelectBenckmark: (benchmark: string) => void;
  benchmarkSelected: string;
  rightIcon?: React.ReactNode;
  showPopup?: boolean;
}

const tickerIcons: TickerIcons = {
  FEZ: <AppIcon icon="EuropeanUnionFlag" />,
  VGK: <AppIcon icon="EuropeanUnionFlag" />,
  DAX: <AppIcon icon="GermanyFlag" />,
  EWQ: <AppIcon icon="FranceFlag" />,
  EWP: <AppIcon icon="SpainFlag" />,
  EWJ: <AppIcon icon="JapanFlag" />,
  EWZ: <AppIcon icon="BrazilFlag" />,
};

export const IndexGroup: FC<IndexGroupProps> = ({
  title,
  indexes,
  benchmarkSelected,
  handleSelectBenckmark,
  rightIcon,
  showPopup = true,
}) => {
  const infoPopups = useInfoPopup();
  return (
    <section>
      <Heading variant="h5" title={title} />
      <div className="boxwrapper mt-[10px] !grid-cols-3 2xl:!grid-cols-4 ">
        {indexes.map(({ key, ticker }) => (
          <SelectItem
            key={key}
            label={formattedBenchmarks[ticker as AllBenchmarks]}
            labelId={ticker}
            checkedValidator={benchmarkSelected}
            groupName="benchmark"
            rightIcon={rightIcon ? rightIcon : tickerIcons[ticker]}
            handleSelectItem={() => {
              handleSelectBenckmark(ticker);
            }}
            rightIconFunction={() =>
              showPopup && infoPopups(`ai-builder.${ticker}` as PopupsName)
            }
            boxTestId={ticker}
          />
        ))}
      </div>
    </section>
  );
};
